import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppWindow,
  Building2,
  Eye,
  ShieldAlert,
  Fingerprint,
  KeyRound,
  MonitorSmartphone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

import { Account, CurrentSession, OperatorPermission, api } from "../api";
import { WorkspaceShell } from "../WorkspaceShell";
import { ThemeSwitcher } from "../theme";
import { AccessPages } from "./AccessPages";
import { ErrorState, LoadingState } from "./components";
import { OrganizationPage } from "./OrganizationPage";
import { PrivacyPage } from "./PrivacyPage";
import { ProfilePage } from "./ProfilePage";
import { SecurityPage } from "./SecurityPage";

const pages = [
  { id: "profile", label: "个人资料", icon: UserRound },
  { id: "security", label: "登录与安全", icon: ShieldCheck },
  { id: "devices", label: "客户端设备", icon: MonitorSmartphone },
  { id: "sessions", label: "登录会话", icon: KeyRound },
  { id: "connections", label: "连接应用", icon: AppWindow },
  { id: "organizations", label: "组织", icon: Building2 },
  { id: "privacy", label: "隐私与数据", icon: Fingerprint },
] as const;

export type AccountPageId = (typeof pages)[number]["id"];

function pageFromHash(): AccountPageId {
  const value = window.location.hash.replace(/^#\/?/, "").split(/[?&]/)[0];
  if (pages.some((page) => page.id === value)) return value as AccountPageId;
  const pathValue = window.location.pathname.match(/^\/account\/([^/]+)$/)?.[1];
  return pages.some((page) => page.id === pathValue) ? pathValue as AccountPageId : "profile";
}

export function AccountCenter({
  session,
  onLoggedOut,
}: {
  session: CurrentSession;
  onLoggedOut: () => void;
}) {
  const [page, setPage] = useState<AccountPageId>(pageFromHash);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const account = useQuery({
    queryKey: ["account"],
    queryFn: () => api<Account>("/api/v1/account"),
    retry: false,
  });
  const operator = useQuery({
    queryKey: ["operator-session"],
    queryFn: () => api<{ permissions: OperatorPermission[] }>("/api/v1/operator/me"),
    retry: false,
  });
  const logout = useMutation({
    mutationFn: () => api<void>("/api/v1/session", { method: "DELETE" }),
    onSettled: () => {
      queryClient.clear();
      onLoggedOut();
    },
  });

  useEffect(() => {
    const changed = () => setPage(pageFromHash());
    window.addEventListener("hashchange", changed);
    if (!window.location.hash) window.history.replaceState(null, "", `#/${pageFromHash()}`);
    return () => window.removeEventListener("hashchange", changed);
  }, []);

  if (account.isPending) return <main className="bootstrap-state"><LoadingState label="正在打开账号中心" /></main>;
  if (account.error || !account.data) {
    return <main className="bootstrap-state"><ErrorState error={account.error} retry={() => void account.refetch()} /></main>;
  }

  if (account.data.status === "DELETION_PENDING") {
    return (
      <main className="pending-account-shell">
        <div className="auth-brand-row"><div className="identity-mark"><Eye aria-hidden="true" /><span>EvilEyeAuth</span></div><ThemeSwitcher /></div>
        <PrivacyPage deletionPending onLoggedOut={onLoggedOut} />
      </main>
    );
  }

  function navigate(next: AccountPageId) {
    window.location.hash = `/${next}`;
    setMenuOpen(false);
  }

  return (
    <WorkspaceShell
      variant="account"
      workspaceLabel="账号中心"
      workspaceDescription="身份资料、凭据与授权连接"
      workspaceIcon={Fingerprint}
      navLabel="个人空间"
      navItems={pages}
      activeId={page}
      onNavigate={navigate}
      account={account.data}
      statusLabel={`AAL${session.assuranceLevel} · ${session.authenticationMethod}`}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      loggingOut={logout.isPending}
      onLogout={() => logout.mutate()}
      showOperator={Boolean(operator.data?.permissions.length)}
      extraLinks={[{ href: `/appeal?from=account&identifier=${encodeURIComponent(account.data.username)}`, label: "提交账号申诉", icon: ShieldAlert }]}
    >
      <AnimatePresence mode="wait">
        <motion.div
          className="page-stage"
          key={page}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
        >
          {page === "profile" && <ProfilePage account={account.data} />}
          {page === "security" && <SecurityPage session={session} />}
          {(page === "devices" || page === "sessions" || page === "connections") && <AccessPages page={page} onLoggedOut={onLoggedOut} />}
          {page === "organizations" && <OrganizationPage />}
          {page === "privacy" && <PrivacyPage onLoggedOut={onLoggedOut} />}
        </motion.div>
      </AnimatePresence>
    </WorkspaceShell>
  );
}
